import React, {
	useState,
	useCallback,
	useEffect,
	useMemo,
} from 'react';
import { EditOutlined } from '@ant-design/icons';
import { Flex, Tooltip } from 'antd';
import Button, { ButtonProps } from '@client/components/Button';
import { useNavigationBlock } from '@client/lib/navigationBlock';
import Details from '@client/components/Details';
import showErrorNotification from '@client/utils/showErrorNotification';
import EditableField, { EditableDetailsItem } from '@client/components/EditableField';
import styles from './EditableDetails.module.css';

type EditableDetailsProps<Values extends object> = {
	items: EditableDetailsItem<Partial<Values>>[];
	onSave?: (values: Partial<Values>) => void | Promise<void>;
	hideHeader?: boolean;
	hideButtons?: boolean;
	editValues?: Partial<Values>;
	editing?: boolean;
	title?: string | React.ReactNode;
	onEditValuesChange?: (values: Partial<Values>) => Promise<void> | void;
	onStartEditing?: () => void;
	onCancel?: () => void;
	showCancelButton?: boolean;
	showSaveButton?: boolean;
	enableAutoSave?: boolean;
	saveButtonProps?: ButtonProps;
	cancelButtonProps?: ButtonProps;
	editButtonProps?: ButtonProps;
	className?: string;
	labelWidth?: number;
	confirmTitle?: string | React.ReactNode;
	blockNavigationOverride?: (values: Partial<Values>) => boolean;
	leftSaveButtonProps?: ButtonProps & {
		tooltip?: React.ReactNode;
	};
};

const EditableDetails = <Values extends object,>({
	items,
	hideButtons,
	hideHeader,
	editValues: editValuesProp,
	editing: editingProp,
	title,
	onEditValuesChange,
	onStartEditing,
	onSave,
	onCancel,
	enableAutoSave = true,
	saveButtonProps = {},
	cancelButtonProps = {},
	showCancelButton = true,
	showSaveButton = true,
	editButtonProps = {},
	confirmTitle,
	className,
	blockNavigationOverride,
	leftSaveButtonProps,
	...props
}: EditableDetailsProps<Values>) => {
	const [editingState, setEditing] = useState(false);
	const [editValuesState, setEditValuesState] = useState<Partial<Values>>({});

	const editing = editingProp != null ? editingProp : editingState;

	const editValues = editValuesProp || editValuesState;
	const setEditValues = useMemo(
		() => onEditValuesChange || setEditValuesState,
		[onEditValuesChange],
	);

	const { useBlocker } = useNavigationBlock();

	const shouldBlock = useMemo(() => {
		return blockNavigationOverride ?
			blockNavigationOverride(editValues) :
			(editing && Object.keys(editValues).length > 0);
	}, [blockNavigationOverride, editValues, editing]);

	useBlocker(shouldBlock);

	const values = useMemo(() => ({
		...items.reduce((obj, item) => ({
			...obj,
			[item.key]: item.value,
		}), {}),
		...editValues,
	}), [items, editValues]);

	const editChange = useCallback(async (fieldName: string, value: string) => {
		return await setEditValues({
			...editValues,
			[fieldName]: value,
		});
	}, [editValues, setEditValues]);

	const saveEdit = useCallback(async () => {
		try {
			if (Object.keys(editValues).length > 0 && typeof onSave === 'function') {
				await onSave(editValues);
			} else if (typeof onSave === 'function') {
				await onSave({});
			}

			setEditing(false);
			await setEditValues({});

			if (typeof onCancel === 'function') {
				onCancel();
			}
		} catch (e) {
			showErrorNotification('Could not save', e as Error);
		}
	}, [onCancel, editValues, setEditValues, onSave]);

	const cancelEdit = useCallback(() => {
		setEditValues({});
		setEditing(false);

		if (typeof onCancel === 'function') {
			onCancel();
		}
	}, [onCancel, setEditValues]);

	// On unmount, auto save if currently editing
	useEffect(() => () => {
		if (enableAutoSave && editing && Object.keys(editValues).length > 0) {
			saveEdit();
		}

		// Only want to run on unmount
	}, []);

	let headerButtons;

	if (editing) {
		headerButtons = (
			<Flex gap={8} align="center" wrap="wrap">
				{showCancelButton && (
					<Button
						key="cancel-button"
						onClick={cancelEdit}
						{...cancelButtonProps}
					>
						Cancel
					</Button>
				)}
				{leftSaveButtonProps && (
					<Tooltip title={leftSaveButtonProps.tooltip}>
						<span>
							<Button
								key="left-save-button"
								{...leftSaveButtonProps}
								title={undefined}
							>
								{leftSaveButtonProps.title}
							</Button>
						</span>
					</Tooltip>
				)}
				{showSaveButton && (
					<Button
						key="save-button"
						type="primary"
						onClick={saveEdit}
						className={styles.saveButton}
						{...saveButtonProps}
						confirmTitle={confirmTitle}
					>
						{saveButtonProps.title ?? 'Save'}
					</Button>
				)}
			</Flex>
		);
	} else {
		headerButtons = (
			<Button
				key="edit-button"
				type="link"
				onClick={() => {
					setEditing(true);
					onStartEditing?.();
				}}
				icon={(
					<EditOutlined
						className={styles.detailsIcon}
					/>
				)}
				{...editButtonProps}
			/>
		);
	}

	const getLabel = (label: string, required?: boolean): string | React.ReactElement => {
		if (!required) {
			return label;
		}

		return (
			<Flex gap={5}>
				<p style={{ fontSize: 14, padding: 0, margin: 0, color: 'red' }}>*</p>
				{label}
			</Flex>
		);
	};

	const detailsItems = useMemo(() => items.filter((i) => (
		i.show !== false &&
		!(typeof i.show === 'function' && !i.show(values, editing))
	)).map((item) => ({
		...item,
		key: item.key,
		// @ts-ignore
		label: getLabel(item.label, item.required),
		value: (
			<EditableField<Partial<Values>>
				item={item}
				editChange={editChange}
				editValues={editValues}
				editing={editing}
			/>
		),
	})), [editChange, editValues, editing, items, values]);

	return (
		<Details
			items={detailsItems}
			title={title}
			hideHeader={hideHeader}
			extra={hideButtons ? undefined : headerButtons}
			{...props}
			className={className}
		/>
	);
};

export default EditableDetails;
